import React from "react";
import { ApolloError } from "@apollo/client";
import { DataCellsSection } from "./DataCellsSection";
import { ChartSection } from "./ChartSection";
import { ListSection } from "./ListSection";
import { PredictiveModelType } from "./types";

interface PredictiveModelWrapperProps {
  orgId: string | undefined;
  orgIdLoading: boolean;
  orgIdError: ApolloError | undefined;
  cohortYear: number | undefined;
  cohortLoading: boolean;
  cohortError: ApolloError | undefined;
  modelType?: PredictiveModelType;
}

/**
 * PredictiveModelWrapper
 *
 * A reusable wrapper component that renders the complete predictive model interface,
 * including data cells, charts, and student lists. This component encapsulates the
 * full predictive model experience and handles data fetching coordination between
 * all three sections.
 *
 * Why this wrapper exists:
 * - **Reusability**: Can be embedded in different pages (main predictive models page,
 *   engagement tab, etc.) without duplicating the layout logic
 * - **Consistency**: Ensures the same spacing, layout, and component arrangement
 *   across all locations where predictive models are displayed
 * - **Data coordination**: Centralizes the passing of org and cohort data to all
 *   child components, reducing prop drilling in parent components
 */
export const PredictiveModelWrapper: React.FC<PredictiveModelWrapperProps> = ({
  orgId,
  orgIdLoading,
  orgIdError,
  cohortYear,
  cohortLoading,
  cohortError,
  modelType = "Commit",
}) => {
  const pageContext = {
    orgId,
    orgIdLoading,
    orgIdError,
    cohortYear,
    cohortLoading,
    cohortError,
  };

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="w-full space-y-6 text-foreground">
        <DataCellsSection
          orgId={orgId}
          orgIdLoading={orgIdLoading}
          orgIdError={orgIdError}
          cohortLoading={cohortLoading}
          cohortError={cohortError}
          modelType={modelType}
        />
        <ChartSection {...pageContext} modelType={modelType} />
        <ListSection
          orgId={orgId}
          orgIdLoading={orgIdLoading}
          orgIdError={orgIdError}
          modelType={modelType}
        />
      </div>
    </div>
  );
};
